import type { ReactElement } from "react"
import { useContext, useState } from "react"
import OnColorContext from "@onestore/hel/dist/contexts/OnColorContext"
import { zIndex } from "@onestore/hel/dist/settings"
import getColor from "@onestore/hel/dist/utils/getColor"
import _t from "@core/i18n"
import FormilyFormModal from "@gatsby-plugin-generic-page/components/FormilyFormModal"
import type { Formily as FormilyType } from "@gatsby-plugin-generic-page/fragments/formily"
import { ComparisonTable } from "@ionos-web-design-system/react"
import { useTheme } from "styled-components"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { ComparableProduct } from "~/fragments/comparableProduct"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import isEmpty from "~/lib/isEmpty"
import type { ComparableProductsPackage, PeriodName } from "~/types/squidex"
import { getProductParameters } from "./helpers/getProductParameters"
import { getTableSections } from "./helpers/getTableSections"
import useHeaderTariffCards from "./hooks/useHeaderTariffCards"
import usePackageColumnsState from "./hooks/usePackageColumnsState"

export type ParametersUDSTableProps = {
  /** List of comparable products to display in the table. Omit when using `packages` — products are derived from the packages in that mode. */
  products?: ComparableProduct[]
  /** Package variants — when provided, each package occupies one column with a variant dropdown. */
  packages?: ComparableProductsPackage[]
  /** Sale configuration of the currently product — drives button rendering, current product detection, and price resolution. */
  saleConfiguration?: SaleConfiguration
  /** Period used to resolve price data and basket/bonus-screen actions. */
  selectedPeriod?: PeriodName
  /** Custom link text below CardTariff button */
  customLinkText?: string
  /** When true, the currently viewed product column shows a label below CardTariff button. */
  showCurrentViewing?: boolean
  /** ID of the product that should be highlighted as the recommended/active column. */
  recommendedComparableProductId?: string
}

export default function ParametersTableUds({
  products = [],
  packages,
  saleConfiguration,
  selectedPeriod,
  customLinkText,
  showCurrentViewing,
  recommendedComparableProductId,
}: ParametersUDSTableProps): ReactElement | null {
  const { currentPriceType } = usePriceTypeContext()
  const [activeFormilyForm, setActiveFormilyForm] =
    useState<FormilyType | null>(null)

  const onFormilyFormClick = (form: FormilyType) => {
    setActiveFormilyForm(form)
  }

  const sectionColor = useContext(OnColorContext)
  const theme = useTheme()
  const backgroundColor = getColor(theme, sectionColor)

  const [currentProducts, dropdowns] = usePackageColumnsState(
    products,
    packages
  )

  const { tariffCardsColumns } = useHeaderTariffCards({
    products: currentProducts,
    dropdowns,
    saleConfiguration,
    selectedPeriod,
    customLinkText,
    onFormilyFormClick,
    showCurrentViewing,
    recommendedComparableProductId,
  })

  const parameters = getProductParameters(currentProducts)

  if (isEmpty(parameters)) {
    return null
  }

  const sections = getTableSections(parameters, currentPriceType)

  return (
    <>
      <ComparisonTable
        tariffCardsColumns={tariffCardsColumns}
        sections={sections}
        showDifferencesLabel={
          tariffCardsColumns.length > 1
            ? _t("toggleLabel.showDifferences")
            : undefined
        }
        style={{ backgroundColor }}
        className="leading-normal"
        stickyHeaderZIndex={zIndex.TableMolHeader}
      />

      {activeFormilyForm ? (
        <FormilyFormModal
          formilyForm={activeFormilyForm}
          isOpen
          onClose={() => setActiveFormilyForm(null)}
        />
      ) : null}
    </>
  )
}
